import dayjs from 'dayjs';
import {
  BUYER_PAN_REGEX,
  IFA_COMMISSION_MODE_AMOUNT,
  IFA_COMMISSION_MODE_PERCENT,
  ISIN_LENGTH,
  PRODUCT_TYPE_OTHER,
} from './offlineProductSales.constants.js';

export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function formatCurrency(value) {
  const n = toNumber(value);
  if (n === null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatDate(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY');
}

export function formatPaidDateDisplay(paidDate, paidDateNote) {
  if (paidDate) return formatDate(paidDate);
  if (paidDateNote) return paidDateNote;
  return '—';
}

export function apiErrMsg(err) {
  return err?.response?.data?.message || err?.message || 'Something went wrong';
}

export function normalizePan(value) {
  return (value || '').trim().toUpperCase();
}

export function normalizeIsin(value) {
  return (value || '').trim().toUpperCase();
}

export function validateBuyerPan(pan, { required = true } = {}) {
  const normalized = normalizePan(pan);
  if (!normalized) {
    return required ? 'Buyer PAN is required' : null;
  }
  if (!BUYER_PAN_REGEX.test(normalized)) {
    return 'Enter a valid PAN (e.g. ABCDE1234F)';
  }
  return null;
}

export function validateIsin(isin, { required = false } = {}) {
  const normalized = normalizeIsin(isin);
  if (!normalized) {
    return required ? 'ISIN is required' : null;
  }
  if (normalized.length !== ISIN_LENGTH) {
    return `ISIN must be exactly ${ISIN_LENGTH} characters`;
  }
  return null;
}

export function validateCreateFields(values, { isEdit = false } = {}) {
  if (isEdit) return null;

  const panError = validateBuyerPan(values.buyer_pan, { required: true });
  if (panError) return panError;

  const isinError = validateIsin(values.isin);
  if (isinError) return isinError;

  if (!values.ifa_partner_uuid) {
    return 'IFA Name is required';
  }

  return null;
}

function resolveIfaCommissionMode(mode) {
  return mode === IFA_COMMISSION_MODE_AMOUNT
    ? IFA_COMMISSION_MODE_AMOUNT
    : IFA_COMMISSION_MODE_PERCENT;
}

export function resolveRevenueMode(gross, ifa, commissionMode = IFA_COMMISSION_MODE_PERCENT) {
  const grossNum = toNumber(gross);
  let ifaNum = toNumber(ifa);
  if (typeof ifa === 'string') {
    ifaNum = parseIfaCommissionInput(ifa, gross, commissionMode).amount;
  }
  if (grossNum !== null && ifaNum !== null) return 'auto';
  return 'manual';
}

/**
 * Derive percentage string for form display / mode-switch from stored gross + IFA amount.
 */
export function ifaAmountToPercentInput(grossRevenue, ifaAmount) {
  const gross = toNumber(grossRevenue);
  const ifa = toNumber(ifaAmount);
  if (gross === null || ifa === null || gross <= 0) return undefined;
  const percent = (ifa / gross) * 100;
  const rounded = Math.round(percent * 10000) / 10000;
  return String(parseFloat(rounded.toFixed(4)));
}

function formatAmountInput(amount) {
  if (amount === null || amount === undefined) return undefined;
  const rounded = Math.round(Number(amount) * 100) / 100;
  return String(parseFloat(rounded.toFixed(2)));
}

/**
 * Parse IFA commission input.
 * - percent: value is % of gross (e.g. 1 → 1%); optional trailing % stripped; needs gross.
 * - amount: value is absolute rupees; gross optional (used only to derive %).
 * Always returns rupee `amount` for API when valid.
 */
export function parseIfaCommissionInput(
  input,
  grossRevenue,
  mode = IFA_COMMISSION_MODE_PERCENT,
) {
  const commissionMode = resolveIfaCommissionMode(mode);

  if (input === null || input === undefined || input === '') {
    return { amount: null, error: null, mode: commissionMode, percent: null };
  }

  const str = String(input).trim().replace(/[₹,\s]/g, '').replace(/%/g, '').trim();
  if (!str) {
    return { amount: null, error: null, mode: commissionMode, percent: null };
  }

  const value = Number(str);
  if (!Number.isFinite(value) || value < 0) {
    return {
      amount: null,
      error:
        commissionMode === IFA_COMMISSION_MODE_AMOUNT
          ? 'Enter a valid amount (e.g. 3000)'
          : 'Enter a valid percentage (e.g. 1 or 2.5)',
      mode: commissionMode,
      percent: null,
    };
  }

  const gross = toNumber(grossRevenue);

  if (commissionMode === IFA_COMMISSION_MODE_AMOUNT) {
    const amount = Math.round(value * 100) / 100;
    let percent = null;
    if (gross !== null && gross > 0) {
      percent = Math.round(((amount / gross) * 100) * 10000) / 10000;
    }
    return { amount, error: null, mode: commissionMode, percent };
  }

  if (gross === null) {
    return {
      amount: null,
      error: 'Enter gross revenue to calculate IFA commission',
      mode: commissionMode,
      percent: value,
    };
  }

  const amount = Math.round(((gross * value) / 100) * 100) / 100;
  return { amount, error: null, mode: commissionMode, percent: value };
}

/**
 * Convert displayed IFA input when toggling % ↔ ₹.
 * Preserves economic value when gross is known; otherwise clears when switching to %.
 */
export function convertIfaCommissionBetweenModes({
  value,
  fromMode,
  toMode,
  grossRevenue,
}) {
  const from = resolveIfaCommissionMode(fromMode);
  const to = resolveIfaCommissionMode(toMode);
  if (from === to) {
    return { value: value == null || value === '' ? undefined : String(value), error: null };
  }

  const parsed = parseIfaCommissionInput(value, grossRevenue, from);
  if (parsed.error && from === IFA_COMMISSION_MODE_PERCENT) {
    // Missing gross while in % — cannot convert meaningfully
    if (to === IFA_COMMISSION_MODE_AMOUNT) {
      return { value: undefined, error: parsed.error };
    }
  }

  if (parsed.amount === null && (value === null || value === undefined || value === '')) {
    return { value: undefined, error: null };
  }

  if (to === IFA_COMMISSION_MODE_AMOUNT) {
    if (parsed.amount === null) {
      return { value: undefined, error: parsed.error || 'Cannot convert to amount' };
    }
    return { value: formatAmountInput(parsed.amount), error: null };
  }

  // to percent
  const gross = toNumber(grossRevenue);
  if (gross === null || gross <= 0) {
    return {
      value: undefined,
      error: 'Enter gross revenue to convert IFA commission to percentage',
    };
  }
  if (parsed.amount === null) {
    return { value: undefined, error: parsed.error || 'Cannot convert to percentage' };
  }
  return { value: ifaAmountToPercentInput(gross, parsed.amount), error: null };
}

export function resolveFormValuesWithIfa(values) {
  const commissionMode = resolveIfaCommissionMode(values.ifa_commission_mode);
  const parsed = parseIfaCommissionInput(
    values.ifa_commission,
    values.gross_revenue,
    commissionMode,
  );
  return {
    ...values,
    ifa_commission: parsed.amount,
  };
}

export function isIfaExceedsGross(gross, ifa) {
  const grossNum = toNumber(gross);
  const ifaNum = toNumber(ifa);
  if (grossNum === null || ifaNum === null) return false;
  return ifaNum > grossNum;
}

export function validateRevenueFields(values) {
  const commissionMode = resolveIfaCommissionMode(values.ifa_commission_mode);
  const parsed = parseIfaCommissionInput(
    values.ifa_commission,
    values.gross_revenue,
    commissionMode,
  );
  if (parsed.error) return parsed.error;

  const resolvedValues = resolveFormValuesWithIfa(values);
  const mode = resolveRevenueMode(
    resolvedValues.gross_revenue,
    resolvedValues.ifa_commission,
    commissionMode,
  );

  if (mode === 'auto') {
    if (isIfaExceedsGross(resolvedValues.gross_revenue, resolvedValues.ifa_commission)) {
      return 'IFA commission cannot exceed gross revenue';
    }
    return null;
  }

  const altcase = toNumber(resolvedValues.altcase_revenue);
  if (altcase === null) {
    return 'Altcase revenue is required when gross revenue or IFA commission is not provided';
  }
  if (altcase < 0) {
    return 'Altcase revenue must be zero or greater';
  }
  return null;
}

function formatDateField(value) {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
  return value;
}

function buildProductTypePayload(values) {
  if (values.product_type === PRODUCT_TYPE_OTHER) {
    return {
      product_type: PRODUCT_TYPE_OTHER,
      custom_product_name: (values.custom_product_name || '').trim(),
    };
  }
  return { product_type: values.product_type };
}

function buildRevenueCreatePayload(values) {
  const resolved = resolveFormValuesWithIfa(values);
  const mode = resolveRevenueMode(resolved.gross_revenue, resolved.ifa_commission);
  if (mode === 'auto') {
    return {
      gross_revenue: toNumber(resolved.gross_revenue),
      ifa_commission: toNumber(resolved.ifa_commission),
    };
  }
  return { altcase_revenue: toNumber(resolved.altcase_revenue) };
}

export function buildCreatePayload(values) {
  const payload = {
    ...buildProductTypePayload(values),
    buyer_name: (values.buyer_name || '').trim(),
    buyer_pan: normalizePan(values.buyer_pan),
    isin: normalizeIsin(values.isin),
    ifa_partner_uuid: values.ifa_partner_uuid,
    deal_date: formatDateField(values.deal_date),
    rm_uuid: values.rm_uuid,
  };

  const securityName = (values.security_name || '').trim();
  if (securityName) payload.security_name = securityName;

  Object.assign(payload, buildRevenueCreatePayload(values));

  if (values.commission_paid_status) {
    payload.commission_paid_status = values.commission_paid_status;
  }

  const paidDate = formatDateField(values.paid_date);
  if (paidDate) payload.paid_date = paidDate;

  const paidDateNote = (values.paid_date_note || '').trim();
  if (paidDateNote) payload.paid_date_note = paidDateNote;

  const remarks = (values.remarks || '').trim();
  if (remarks) payload.remarks = remarks;

  return payload;
}

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a ?? '') === String(b ?? '');
}

function buildRevenueUpdatePayload(values, initialRecord) {
  const resolved = resolveFormValuesWithIfa(values);
  const currentMode = resolveRevenueMode(resolved.gross_revenue, resolved.ifa_commission);
  const initialGross = toNumber(initialRecord?.gross_revenue);
  const initialIfa = toNumber(initialRecord?.ifa_commission);
  const initialAltcase = toNumber(initialRecord?.altcase_revenue);

  const currentGross = toNumber(resolved.gross_revenue);
  const currentIfa = toNumber(resolved.ifa_commission);
  const currentAltcase = toNumber(resolved.altcase_revenue);

  const initialWasAuto = initialGross !== null && initialIfa !== null;
  const payload = {};

  if (currentMode === 'auto') {
    if (!valuesEqual(currentGross, initialGross) || !valuesEqual(currentIfa, initialIfa)) {
      payload.gross_revenue = currentGross;
      payload.ifa_commission = currentIfa;
    }
  } else {
    const switchedFromAuto = initialWasAuto;
    if (switchedFromAuto) {
      payload.gross_revenue = null;
      payload.ifa_commission = null;
      payload.altcase_revenue = currentAltcase;
    } else {
      if (initialGross !== null && currentGross === null) payload.gross_revenue = null;
      if (initialIfa !== null && currentIfa === null) payload.ifa_commission = null;
      if (!valuesEqual(currentAltcase, initialAltcase)) {
        payload.altcase_revenue = currentAltcase;
      }
    }
  }

  return payload;
}

export function buildUpdatePayload(values, initialRecord) {
  const payload = {};

  if (values.product_type === PRODUCT_TYPE_OTHER) {
    const customName = (values.custom_product_name || '').trim();
    if (customName !== (initialRecord?.product_type || '')) {
      payload.product_type = PRODUCT_TYPE_OTHER;
      payload.custom_product_name = customName;
    }
  } else if (values.product_type !== initialRecord?.product_type) {
    payload.product_type = values.product_type;
  }

  const buyerName = (values.buyer_name || '').trim();
  if (buyerName !== (initialRecord?.buyer_name || '')) {
    payload.buyer_name = buyerName;
  }

  const buyerPan = normalizePan(values.buyer_pan);
  const initialPan = normalizePan(initialRecord?.buyer_pan);
  if (buyerPan !== initialPan) {
    payload.buyer_pan = buyerPan || null;
  }

  const isin = normalizeIsin(values.isin);
  const initialIsin = normalizeIsin(initialRecord?.isin);
  if (isin !== initialIsin) {
    payload.isin = isin || null;
  }

  if (values.ifa_partner_uuid !== initialRecord?.ifa?.uuid) {
    payload.ifa_partner_uuid = values.ifa_partner_uuid || null;
  }

  const securityName = (values.security_name || '').trim();
  const initialSecurity = (initialRecord?.security_name || '').trim();
  if (securityName !== initialSecurity) {
    payload.security_name = securityName || null;
  }

  const dealDate = formatDateField(values.deal_date);
  if (dealDate !== initialRecord?.deal_date) {
    payload.deal_date = dealDate;
  }

  if (values.rm_uuid && values.rm_uuid !== initialRecord?.rm?.uuid) {
    payload.rm_uuid = values.rm_uuid;
  }

  Object.assign(payload, buildRevenueUpdatePayload(values, initialRecord));

  if (values.commission_paid_status !== initialRecord?.commission_paid_status) {
    payload.commission_paid_status = values.commission_paid_status || null;
  }

  const paidDate = formatDateField(values.paid_date);
  if (paidDate !== (initialRecord?.paid_date || undefined)) {
    payload.paid_date = paidDate || null;
  }

  const paidDateNote = (values.paid_date_note || '').trim();
  const initialNote = (initialRecord?.paid_date_note || '').trim();
  if (paidDateNote !== initialNote) {
    payload.paid_date_note = paidDateNote || null;
  }

  const remarks = (values.remarks || '').trim();
  const initialRemarks = (initialRecord?.remarks || '').trim();
  if (remarks !== initialRemarks) {
    payload.remarks = remarks || null;
  }

  return payload;
}

export function recordToFormValues(record) {
  if (!record) return {};

  const ifaAmount = toNumber(record.ifa_commission);

  return {
    product_type: record.product_type,
    custom_product_name: undefined,
    buyer_name: record.buyer_name,
    buyer_pan: record.buyer_pan || undefined,
    isin: record.isin || undefined,
    ifa_partner_uuid: record.ifa?.uuid ?? undefined,
    security_name: record.security_name || undefined,
    deal_date: record.deal_date ? dayjs(record.deal_date) : null,
    rm_uuid: record.rm?.uuid,
    gross_revenue: record.gross_revenue ?? undefined,
    ifa_commission_mode: IFA_COMMISSION_MODE_AMOUNT,
    ifa_commission: ifaAmount === null ? undefined : formatAmountInput(ifaAmount),
    altcase_revenue: record.altcase_revenue ?? undefined,
    commission_paid_status: record.commission_paid_status || undefined,
    paid_date: record.paid_date ? dayjs(record.paid_date) : null,
    paid_date_note: record.paid_date_note || undefined,
    remarks: record.remarks || undefined,
  };
}

export function filterProductTypesForDropdown(productTypes) {
  return (productTypes || []).filter(Boolean);
}

export function filterProductTypesForFilter(productTypes) {
  return (productTypes || []).filter((t) => t && t !== PRODUCT_TYPE_OTHER);
}
